// MĂRCILE de armătură — un singur loc care știe cum arată una și ce spune
// sufixul ei (oglinda lui `structure/planse/comun.py::SUFIX_MARCA`).
//
// O marcă e numele de pe planșă al unui TIP de bară sau de etrier: un număr
// plus sufixul familiei de marcă. La familiile «de bară» sufixul stă după
// număr (`3g`, `7gF`, `1s`, `2i`), la cele «de etrier» și la barele
// verticale / orizontale înaintea lui (`e1`, `c2`, `v3`, `o1`). Numărul e
// CONTIGUU în familie (1..n, dat de consolidarea finală a tipurilor —
// `componente.consolideaza_tipuri`), nu id-ul tipului: de la marcă la rândul
// de tip se ajunge prin `marca` / `marci` ale rândului
// (`MarcaArmaturaPage.tipulMarcii`), iar de la poziție la marcă prin `marca`
// scrisă pe ea. Numărul e id-ul tipului numai pe un extras vechi, fără
// mărci scrise — atunci se compune de aici (`marcaTipului`, `marcaPozitiei`).

import type { Cheie } from "../i18n";

/** Familia de marcă: ce fel de bară / etrier numește sufixul. */
export type FamilieMarca = "grinda" | "fundatie" | "placa_sus" | "placa_jos"
  | "vertical" | "orizontal" | "etrier" | "agrafa";

const SUFIX: Record<string, FamilieMarca> = {
  g: "grinda", gF: "fundatie", s: "placa_sus", i: "placa_jos",
  v: "vertical", o: "orizontal", e: "etrier", c: "agrafa",
};

/** Forma unei mărci: `3g`, `7gF`, `1s`, `2i`, `e1`, `v2`, `o1`, `c1`. */
export const RX_MARCA = /^(\d+(g|gF|s|i)|[evoc]\d+)$/;

export function esteMarca(s: string | null | undefined): boolean {
  return RX_MARCA.test(String(s ?? "").trim());
}

/** Familia și numărul dintr-o marcă; `null` dacă nu e o marcă. `tip` e
 *  numărul mărcii — id-ul tipului numai pe un extras fără mărci scrise. */
export function citesteMarca(marca: string | null | undefined):
    { familie: FamilieMarca; tip: string } | null {
  const m = /^(?:(\d+)(g|gF|s|i)|([evoc])(\d+))$/.exec(String(marca ?? "").trim());
  if (!m) return null;
  return m[1]
    ? { familie: SUFIX[m[2]], tip: String(Number(m[1])) }
    : { familie: SUFIX[m[3]], tip: String(Number(m[4])) };
}

/** Categoria registrului în care stă familia: etrierii și agrafele sunt
 *  «etrieri», restul «bare» (ca `planse/marci.py::categoria_marcii`). */
export function categoriaMarcii(marca: string | null | undefined): "bare" | "etrieri" | null {
  const c = citesteMarca(marca);
  if (!c) return null;
  return c.familie === "etrier" || c.familie === "agrafa" ? "etrieri" : "bare";
}

/** Marca unui tip din registru când pasul nu i-a scris-o încă: etrierii
 *  `e{tip}`, barele `{tip}g`. */
export function marcaTipului(tip: string | number, etrieri: boolean): string {
  return etrieri ? `e${tip}` : `${tip}g`;
}

/** Marca unei POZIȚII din extras: cea scrisă de pas, altfel compusă din
 *  `tip` și din felul elementului care o poartă (grindă `g`, ax de fundare
 *  `gF`, stâlp / lamelă `v`, ochi de placă `i` sau `s` după rol); etrierii
 *  și agrafele sunt `e`. `null` fără `tip`. */
export function marcaPozitiei(p: { tip?: string | null; marca?: string | null;
                                   categorie?: string; rol?: string },
                              felElement?: string | null): string | null {
  if (p.marca) return p.marca;
  if (p.tip == null || p.tip === "") return null;
  if (p.categorie === "etrieri") return `e${p.tip}`;
  switch (felElement) {
    case "ax": return `${p.tip}gF`;
    case "stalp": case "lamela": case "perete": return `v${p.tip}`;
    case "ochi": case "placa_sol":
      return /sus|super/i.test(p.rol ?? "") ? `${p.tip}s` : `${p.tip}i`;
    default: return `${p.tip}g`;
  }
}

/** Cheia de catalog a numelui familiei de marcă. */
export const NUME_FAMILIE_MARCA: Record<FamilieMarca, Cheie> = {
  grinda: "struct.pa.fam_grinda",
  fundatie: "struct.pa.fam_fundatie",
  placa_sus: "struct.pa.fam_placa_sus",
  placa_jos: "struct.pa.fam_placa_jos",
  vertical: "struct.pa.fam_vertical",
  orizontal: "struct.pa.fam_orizontal",
  etrier: "struct.pa.fam_etrier",
  agrafa: "struct.pa.fam_agrafa",
};
